import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SchoolIcon from '@mui/icons-material/School';
import QuizIcon from '@mui/icons-material/Quiz';
import StyleIcon from '@mui/icons-material/Style';
import ChatIcon from '@mui/icons-material/Chat';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SourceIcon from '@mui/icons-material/Source';
import { RAG_CONFIG } from '../config';

const RAGEnhancedAITest = () => {
  const [activeTab, setActiveTab] = useState('study');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({});
  const [error, setError] = useState('');
  
  // Study Content State
  const [studyTopic, setStudyTopic] = useState('');
  const [studySubject, setStudySubject] = useState('');
  const [studyClass, setStudyClass] = useState('');
  const [studyChapter, setStudyChapter] = useState('');
  const [contentType, setContentType] = useState('explanation');
  const [contentTypes, setContentTypes] = useState([]);
  
  // Quiz State
  const [quizTopic, setQuizTopic] = useState('');
  const [quizSubject, setQuizSubject] = useState('');
  const [quizClass, setQuizClass] = useState('');
  const [quizChapter, setQuizChapter] = useState('');
  const [questionType, setQuestionType] = useState('multiple_choice');
  const [difficulty, setDifficulty] = useState('medium');
  const [questionTypes, setQuestionTypes] = useState([]);
  const [difficulties, setDifficulties] = useState([]);
  
  // Flashcard State
  const [flashcardTopic, setFlashcardTopic] = useState('');
  const [flashcardSubject, setFlashcardSubject] = useState('');
  const [flashcardClass, setFlashcardClass] = useState('');
  const [flashcardChapter, setFlashcardChapter] = useState('');
  
  // Chat State
  const [chatMessage, setChatMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [chatSubject, setChatSubject] = useState('');
  const [chatClass, setChatClass] = useState('');

  // Load available options
  useEffect(() => {
    loadContentTypes();
    loadQuestionTypes();
  }, []);

  const loadContentTypes = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/rag-ai/content-types`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${process.env.REACT_APP_API_KEY || 'test-key'}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setContentTypes(data.content_types || []);
      }
    } catch (error) {
      console.error('Failed to load content types:', error);
    }
  };

  const loadQuestionTypes = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/rag-ai/question-types`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${process.env.REACT_APP_API_KEY || 'test-key'}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setQuestionTypes(data.question_types || []);
        setDifficulties(data.difficulties || []);
      }
    } catch (error) {
      console.error('Failed to load question types:', error);
    }
  };

  const generateStudyContent = async () => {
    if (!studyTopic.trim() || !studySubject.trim() || !studyClass.trim()) return;

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/rag-ai/study-content`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_API_KEY || 'test-key'}`
        },
        body: JSON.stringify({
          topic: studyTopic,
          subject: studySubject,
          class_level: studyClass,
          chapter: studyChapter || undefined,
          content_type: contentType,
          user_id: 'test-user-id'
        })
      });

      if (!response.ok) {
        throw new Error(`Study content generation failed: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setResults(prev => ({ ...prev, study: data }));
      } else {
        setError(data.error || 'Study content generation failed');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const generateQuiz = async () => {
    if (!quizTopic.trim() || !quizSubject.trim() || !quizClass.trim()) return;

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/rag-ai/quiz`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_API_KEY || 'test-key'}`
        },
        body: JSON.stringify({
          topic: quizTopic,
          subject: quizSubject,
          class_level: quizClass,
          chapter: quizChapter || undefined,
          question_type: questionType,
          difficulty: difficulty,
          user_id: 'test-user-id'
        })
      });

      if (!response.ok) {
        throw new Error(`Quiz generation failed: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setResults(prev => ({ ...prev, quiz: data }));
      } else {
        setError(data.error || 'Quiz generation failed');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const generateFlashcard = async () => {
    if (!flashcardTopic.trim() || !flashcardSubject.trim() || !flashcardClass.trim()) return;

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/rag-ai/flashcard`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_API_KEY || 'test-key'}`
        },
        body: JSON.stringify({
          topic: flashcardTopic,
          subject: flashcardSubject,
          class_level: flashcardClass,
          chapter: flashcardChapter || undefined,
          user_id: 'test-user-id'
        })
      });

      if (!response.ok) {
        throw new Error(`Flashcard generation failed: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setResults(prev => ({ ...prev, flashcard: data }));
      } else {
        setError(data.error || 'Flashcard generation failed');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const sendChatMessage = async () => {
    if (!chatMessage.trim()) return;

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/rag-ai/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_API_KEY || 'test-key'}`
        },
        body: JSON.stringify({
          message: chatMessage,
          user_id: 'test-user-id',
          subject: chatSubject || undefined,
          class_level: chatClass || undefined
        })
      });

      if (!response.ok) {
        throw new Error(`Chat failed: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.success) {
        const newMessage = {
          user: chatMessage,
          assistant: data.response,
          sources: data.sources,
          confidence: data.confidence,
          context_used: data.context_used,
          timestamp: new Date().toISOString()
        };
        
        setChatHistory(prev => [...prev, newMessage]);
        setChatMessage('');
        setResults(prev => ({ ...prev, chat: data }));
      } else {
        setError(data.error || 'Chat failed');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const renderStudyContent = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <input
          type="text"
          value={studyTopic}
          onChange={(e) => setStudyTopic(e.target.value)}
          placeholder="Enter study topic..."
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        />
        <select
          value={contentType}
          onChange={(e) => setContentType(e.target.value)}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        >
          {contentTypes.map(type => (
            <option key={type.value} value={type.value}>{type.label}</option>
          ))}
        </select>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <input
          type="text"
          value={studySubject}
          onChange={(e) => setStudySubject(e.target.value)}
          placeholder="Subject"
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        />
        <input
          type="text"
          value={studyClass}
          onChange={(e) => setStudyClass(e.target.value)}
          placeholder="Class"
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        />
        <input
          type="text"
          value={studyChapter}
          onChange={(e) => setStudyChapter(e.target.value)}
          placeholder="Chapter (optional)"
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        />
      </div>
      
      <button
        onClick={generateStudyContent}
        disabled={loading || !studyTopic.trim()}
        className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white rounded-lg transition-colors"
      >
        {loading ? 'Generating...' : 'Generate Study Content'}
      </button>
      
      {results.study && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-md border border-gray-200 dark:border-gray-700"
        >
          <div className="flex items-center gap-2 mb-3">
            <span className="text-sm font-medium text-purple-600 dark:text-purple-400">
              Confidence: {(results.study.confidence * 100).toFixed(1)}%
            </span>
            {results.study.context_used && (
              <span className="text-xs bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-2 py-1 rounded">
                RAG Context Used
              </span>
            )}
          </div>
          <div className="prose dark:prose-invert max-w-none">
            <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
              {results.study.content}
            </p>
          </div>
          {results.study.sources && results.study.sources.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Sources:</h4>
              <div className="space-y-1">
                {results.study.sources.map((source, index) => (
                  <div key={index} className="text-xs text-gray-600 dark:text-gray-400">
                    📄 {source.file_name} - {source.subject} Class {source.class_level}
                    {source.chapter && ` (${source.chapter})`}
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );

  const renderQuiz = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <input
          type="text"
          value={quizTopic}
          onChange={(e) => setQuizTopic(e.target.value)}
          placeholder="Enter quiz topic..."
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        />
        <select
          value={questionType}
          onChange={(e) => setQuestionType(e.target.value)}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        >
          {questionTypes.map(type => (
            <option key={type.value} value={type.value}>{type.label}</option>
          ))}
        </select>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <input
          type="text"
          value={quizSubject}
          onChange={(e) => setQuizSubject(e.target.value)}
          placeholder="Subject"
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        />
        <input
          type="text"
          value={quizClass}
          onChange={(e) => setQuizClass(e.target.value)}
          placeholder="Class"
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        />
        <select
          value={difficulty}
          onChange={(e) => setDifficulty(e.target.value)}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        >
          {difficulties.map(diff => (
            <option key={diff.value} value={diff.value}>{diff.label}</option>
          ))}
        </select>
      </div>
      
      <input
        type="text"
        value={quizChapter}
        onChange={(e) => setQuizChapter(e.target.value)}
        placeholder="Chapter (optional)"
        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
      />
      
      <button
        onClick={generateQuiz}
        disabled={loading || !quizTopic.trim()}
        className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg transition-colors"
      >
        {loading ? 'Generating...' : 'Generate Quiz Question'}
      </button>
      
      {results.quiz && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-md border border-gray-200 dark:border-gray-700"
        >
          <div className="flex items-center gap-2 mb-3">
            <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
              Confidence: {(results.quiz.confidence * 100).toFixed(1)}%
            </span>
            {results.quiz.context_used && (
              <span className="text-xs bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-2 py-1 rounded">
                RAG Context Used
              </span>
            )}
          </div>
          
          <div className="space-y-3">
            <div>
              <h4 className="font-medium text-gray-900 dark:text-white mb-2">Question:</h4>
              <p className="text-gray-700 dark:text-gray-300">{results.quiz.question.question}</p>
            </div>
            
            {results.quiz.question.options && results.quiz.question.options.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">Options:</h4>
                <div className="space-y-1">
                  {results.quiz.question.options.map((option, index) => (
                    <div key={index} className="text-gray-700 dark:text-gray-300">
                      {String.fromCharCode(65 + index)}. {option}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div>
              <h4 className="font-medium text-gray-900 dark:text-white mb-2">Correct Answer:</h4>
              <p className="text-green-600 dark:text-green-400">{results.quiz.question.correct_answer}</p>
            </div>
            
            <div>
              <h4 className="font-medium text-gray-900 dark:text-white mb-2">Explanation:</h4>
              <p className="text-gray-700 dark:text-gray-300">{results.quiz.question.explanation}</p>
            </div>
          </div>
          
          {results.quiz.sources && results.quiz.sources.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Sources:</h4>
              <div className="space-y-1">
                {results.quiz.sources.map((source, index) => (
                  <div key={index} className="text-xs text-gray-600 dark:text-gray-400">
                    📄 {source.file_name} - {source.subject} Class {source.class_level}
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );

  const renderFlashcard = () => (
    <div className="space-y-4">
      <input
        type="text"
        value={flashcardTopic}
        onChange={(e) => setFlashcardTopic(e.target.value)}
        placeholder="Enter flashcard topic..."
        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
      />
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <input
          type="text"
          value={flashcardSubject}
          onChange={(e) => setFlashcardSubject(e.target.value)}
          placeholder="Subject"
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        />
        <input
          type="text"
          value={flashcardClass}
          onChange={(e) => setFlashcardClass(e.target.value)}
          placeholder="Class"
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        />
        <input
          type="text"
          value={flashcardChapter}
          onChange={(e) => setFlashcardChapter(e.target.value)}
          placeholder="Chapter (optional)"
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        />
      </div>
      
      <button
        onClick={generateFlashcard}
        disabled={loading || !flashcardTopic.trim()}
        className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg transition-colors"
      >
        {loading ? 'Generating...' : 'Generate Flashcard'}
      </button>
      
      {results.flashcard && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-md border border-gray-200 dark:border-gray-700"
        >
          <div className="flex items-center gap-2 mb-3">
            <span className="text-sm font-medium text-green-600 dark:text-green-400">
              Confidence: {(results.flashcard.confidence * 100).toFixed(1)}%
            </span>
            {results.flashcard.context_used && (
              <span className="text-xs bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-2 py-1 rounded">
                RAG Context Used
              </span>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 dark:text-white mb-2">Front:</h4>
              <p className="text-gray-700 dark:text-gray-300">{results.flashcard.flashcard.front}</p>
            </div>
            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 dark:text-white mb-2">Back:</h4>
              <p className="text-gray-700 dark:text-gray-300">{results.flashcard.flashcard.back}</p>
            </div>
          </div>
          
          {results.flashcard.sources && results.flashcard.sources.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Sources:</h4>
              <div className="space-y-1">
                {results.flashcard.sources.map((source, index) => (
                  <div key={index} className="text-xs text-gray-600 dark:text-gray-400">
                    📄 {source.file_name} - {source.subject} Class {source.class_level}
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );

  const renderChat = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <input
          type="text"
          value={chatSubject}
          onChange={(e) => setChatSubject(e.target.value)}
          placeholder="Subject (optional)"
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        />
        <input
          type="text"
          value={chatClass}
          onChange={(e) => setChatClass(e.target.value)}
          placeholder="Class (optional)"
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        />
      </div>
      
      <div className="flex gap-2">
        <input
          type="text"
          value={chatMessage}
          onChange={(e) => setChatMessage(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
          placeholder="Type your message..."
          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        />
        <button
          onClick={sendChatMessage}
          disabled={loading || !chatMessage.trim()}
          className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white rounded-lg transition-colors"
        >
          {loading ? '...' : 'Send'}
        </button>
      </div>
      
      {/* Chat History */}
      {chatHistory.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-md border border-gray-200 dark:border-gray-700 max-h-96 overflow-y-auto">
          <h4 className="font-medium text-gray-900 dark:text-white mb-3">Chat History:</h4>
          <div className="space-y-4">
            {chatHistory.map((message, index) => (
              <div key={index} className="space-y-2">
                <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-3">
                  <p className="text-sm text-gray-600 dark:text-gray-400">You:</p>
                  <p className="text-gray-900 dark:text-white">{message.user}</p>
                </div>
                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <p className="text-sm text-gray-600 dark:text-gray-400">Assistant:</p>
                    <span className="text-xs text-purple-600 dark:text-purple-400">
                      {(message.confidence * 100).toFixed(1)}% confidence
                    </span>
                    {message.context_used && (
                      <span className="text-xs bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-1 rounded">
                        RAG
                      </span>
                    )}
                  </div>
                  <p className="text-gray-900 dark:text-white">{message.assistant}</p>
                  {message.sources && message.sources.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                      <p className="text-xs text-gray-500">Sources: {message.sources.length} document(s)</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const tabs = [
    { id: 'study', label: 'Study Content', icon: SchoolIcon },
    { id: 'quiz', label: 'Quiz Generation', icon: QuizIcon },
    { id: 'flashcard', label: 'Flashcards', icon: StyleIcon },
    { id: 'chat', label: 'AI Chat', icon: ChatIcon }
  ];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6">
      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
        🤖 RAG-Enhanced AI Features Test
      </h3>
      
      {/* Tab Navigation */}
      <div className="flex space-x-1 mb-6 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-white dark:bg-gray-600 text-purple-600 dark:text-purple-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>
      
      {/* Error Display */}
      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg mb-4">
          <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
        </div>
      )}
      
      {/* Tab Content */}
      <div className="min-h-[400px]">
        {activeTab === 'study' && renderStudyContent()}
        {activeTab === 'quiz' && renderQuiz()}
        {activeTab === 'flashcard' && renderFlashcard()}
        {activeTab === 'chat' && renderChat()}
      </div>
      
      {/* Configuration Info */}
      <div className="mt-6 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
        <h5 className="font-medium text-purple-900 dark:text-purple-100 mb-2">AI Configuration</h5>
        <div className="text-xs text-purple-700 dark:text-purple-300 space-y-1">
          <p><strong>Generation Model:</strong> {RAG_CONFIG.generation_model}</p>
          <p><strong>Embedding Model:</strong> {RAG_CONFIG.embedding_model}</p>
          <p><strong>Max Tokens:</strong> {RAG_CONFIG.max_tokens}</p>
          <p><strong>Similarity Threshold:</strong> {RAG_CONFIG.similarity_threshold}</p>
        </div>
      </div>
    </div>
  );
};

export default RAGEnhancedAITest; 